import { User } from '@/dataV3/User/User'
import { RegistrationType } from '@/dataV3/User/ConnectedUser'
import { Petsitter } from '@/dataV3/PetSitter/Petsitter'

/** Message sent when the app needs to track a custom event through a native SDK */
export type MessageEventData_TrackEvent = {
  // 2 events used with same params here, because actions will be different on mobile SDKs
  event: 'trackFirebaseEvent' | 'trackFacebookEvent'
  // The event name sent
  eventName: string
  // Additional params sent through the event
  params?: Record<string, string>
}

export type UserData = {
  email?: string
  firstName?: string
  lastName?: string
  phone?: string
  dateOfBirth?: string
  gender?: 'm' | 'f'
  city?: string
  state?: string
  zip?: string
  country?: string
}

export type MessageEventData_Login = {
  // 4 events used with same params here, because actions will be different on mobile SDKs
  event: 'login' | 'reconnect' | 'register' | 'finishRegistration'
  userId: string
  userRole?: 'dog owner' | 'borrower' | 'pet sitter'
  premium?: boolean
  // Used for Facebook
  userData?: UserData
}

export type MessageEventData_StartCheckout = {
  event: 'startCheckout'
  amount: number
  currencyCode: string
}

export type MessageEventData_Purchase = {
  event: 'purchase'
  amount: number
  currencyCode: string
  method: string
}

export type MessageEventData_PurchaseFailed = {
  event: 'purchaseFailed'
  message: string
  code: string
  description?: string
}

export type MessageEventData_Logout = {
  event: 'logout'
}

export type MessageEventData_ShowNotation = {
  event: 'showNotation'
}
export type MessageEventData_OpenURL = {
  event: 'openURL'
  url?: string
}

export type MessageEventData_ScreenView = {
  event: 'screenView'
  screenName?: string
  screenClass?: string
}

export type MessageEventData_FromWebApp =
  | MessageEventData_TrackEvent
  | MessageEventData_Login
  | MessageEventData_StartCheckout
  | MessageEventData_Purchase
  | MessageEventData_PurchaseFailed
  | MessageEventData_Logout
  | MessageEventData_ShowNotation
  | MessageEventData_OpenURL
export const computeMessageEventData_Login = (event: MessageEventData_Login['event'], user: User | Petsitter) => {
  let userData: MessageEventData_Login['userData']
  switch (user['@type']) {
    case 'ConnectedUser':
      userData = {
        email: user.settings.email,
        firstName: user.settings.firstname,
        lastName: user.settings.lastname,
        phone: user.settings.phoneNumber,
        city: user.settings.address?.city,
        zip: user.settings.address?.zipCode,
        country: user.settings.address?.country,
      }
      break
    case 'PendingAcquisitionUser':
      userData = {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || undefined,
        city: user.address?.city,
        zip: user.address?.zipCode,
        country: user.address?.country,
      }
      break
  }
  const eventData: MessageEventData_Login = {
    event,
    userId: user.id,
    userRole:
      user.registrationType === RegistrationType.petowner
        ? 'dog owner'
        : user.registrationType === RegistrationType.borrower
        ? 'borrower'
        : user.registrationType === RegistrationType.petsitter
        ? 'pet sitter'
        : undefined,
    userData,
  }

  return eventData
}
